/* assets/a24-product-sort.js */
(() => {
  'use strict';

  if (customElements.get('a24-product-sort')) return;

  const PS_SORT_VALUES = new Set(['ps-ascending', 'ps-descending']);
  const DEFAULT_PAGE_SIZE = 24;

  class A24ProductSort extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = this.querySelector('[data-a24-product-sort-form]');
      this.select = this.querySelector('[data-a24-product-sort-select]');

      if (!this.form || !this.select) return;

      const selectedValue = String(this.select.value || '').trim();
      this.lastNativeSortValue = this.isPsSortValue(selectedValue) ? '' : selectedValue;
      this.psSortState = null;
      this.psSortRequestId = 0;
      this.psSortInFlight = false;

      this.onSelectChange = this.handleSelectChange.bind(this);
      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this.onFormSubmit = this.handleFormSubmit.bind(this);
      this.select.addEventListener('change', this.onSelectChange);
      this.form.addEventListener('submit', this.onFormSubmit, true);
      document.addEventListener('click', this.onDocumentClick);

      if (this.isPsSortValue(selectedValue)) {
        this.applyPsSort(selectedValue, { keepCurrentPage: true });
      }
    }

    disconnectedCallback() {
      if (this.select && this.onSelectChange) {
        this.select.removeEventListener('change', this.onSelectChange);
      }

      if (this.onDocumentClick) {
        document.removeEventListener('click', this.onDocumentClick);
      }

      if (this.form && this.onFormSubmit) {
        this.form.removeEventListener('submit', this.onFormSubmit, true);
      }
    }

    handleSelectChange() {
      const selectedValue = String(this.select.value || '').trim();

      if (this.isPsSortValue(selectedValue)) {
        this.showLoading('Sortierung wird geladen...');
        this.applyPsSort(selectedValue, { keepCurrentPage: false });
        return;
      }

      this.lastNativeSortValue = selectedValue;
      this.form.querySelectorAll('input[name="page"]').forEach((input) => input.remove());
      this.showLoading('Sortierung wird geladen...');
      this.form.requestSubmit();
    }

    handleFormSubmit(event) {
      const selectedValue = String(this.select?.value || '').trim();
      if (!this.isPsSortValue(selectedValue)) return;

      // PS-Sortierung ist rein AJAX-basiert und darf keinen nativen GET-Submit auslösen.
      event.preventDefault();
      event.stopImmediatePropagation();

      if (this.psSortInFlight) return;

      this.showLoading('Sortierung wird geladen...');
      this.applyPsSort(selectedValue, { keepCurrentPage: false });
    }

    isPsSortValue(value) {
      return PS_SORT_VALUES.has(String(value || '').trim());
    }

    async applyPsSort(sortValue, options = {}) {
      const requestId = ++this.psSortRequestId;
      const keepCurrentPage = Boolean(options.keepCurrentPage);
      this.psSortInFlight = true;

      try {
        if (this.select) this.select.disabled = true;

        const baseUrl = this.buildRequestBaseUrl();
        const cardItems = await this.fetchSortedCardItems(baseUrl);
        if (requestId !== this.psSortRequestId) return;

        this.sortCardItems(cardItems, sortValue);

        const grid = this.getGridElement();
        if (!grid) return;

        const pageSize = this.getPageSizeFromGrid(grid);
        const totalPages = Math.max(1, Math.ceil(cardItems.length / pageSize));

        this.psSortState = {
          active: true,
          sortValue,
          cardItems,
          pageSize,
          totalPages,
          currentPage: 1
        };

        const initialPage = keepCurrentPage ? this.readPageFromUrl() : 1;
        this.renderPsSortedPage(initialPage, { syncHistory: true });
      } catch (error) {
        if (requestId !== this.psSortRequestId) return;

        console.warn('A24: PS-Sortierung konnte nicht per AJAX angewendet werden.', error);

        const fallbackSort = this.findFirstNativeSortValue();
        if (fallbackSort) {
          this.select.value = fallbackSort;
          this.lastNativeSortValue = fallbackSort;
        }
      } finally {
        if (requestId === this.psSortRequestId && this.select) {
          this.select.disabled = false;
        }

        if (requestId === this.psSortRequestId) {
          this.hideLoading();
          this.psSortInFlight = false;
        }
      }
    }

    showLoading(message) {
      if (window.A24SearchRouting?.showLoading) {
        window.A24SearchRouting.showLoading(message);
        return;
      }

      document.body.classList.add('a24-navigation-is-loading');
    }

    hideLoading() {
      document.body.classList.remove('a24-navigation-is-loading');
      const overlay = document.querySelector('[data-a24-navigation-loading]');
      if (overlay) overlay.remove();
    }

    findFirstNativeSortValue() {
      if (!this.select) return '';

      const option = Array.from(this.select.options).find((candidate) => {
        return !this.isPsSortValue(candidate.value);
      });

      return option ? String(option.value || '') : '';
    }

    buildRequestBaseUrl() {
      const action = this.form?.getAttribute('action') || window.location.pathname;
      const url = new URL(action, window.location.origin);
      const formData = new FormData(this.form);

      formData.forEach((value, name) => {
        if (value === null || value === undefined || String(value).trim() === '') return;
        url.searchParams.append(name, String(value));
      });

      url.searchParams.delete('page');

      if (this.lastNativeSortValue && !this.isPsSortValue(this.lastNativeSortValue)) {
        url.searchParams.set('sort_by', this.lastNativeSortValue);
      } else {
        url.searchParams.delete('sort_by');
      }

      return url;
    }

    async fetchSortedCardItems(baseUrl) {
      const firstPage = await this.fetchPageData(baseUrl, 1);
      let runningIndex = 0;
      const cardItems = firstPage.cardItems.map((item) => ({
        html: item.html,
        ps: item.ps,
        sourceIndex: runningIndex++
      }));

      const totalPages = Math.max(1, firstPage.totalPages);
      if (totalPages <= 1) return cardItems;

      const pageNumbers = Array.from({ length: totalPages - 1 }, (_entry, index) => index + 2);
      const batchSize = 4;

      for (let start = 0; start < pageNumbers.length; start += batchSize) {
        const batch = pageNumbers.slice(start, start + batchSize);
        const results = await Promise.all(batch.map((pageNumber) => this.fetchPageData(baseUrl, pageNumber)));

        results.forEach((pageData) => {
          pageData.cardItems.forEach((item) => {
            cardItems.push({
              html: item.html,
              ps: item.ps,
              sourceIndex: runningIndex++
            });
          });
        });
      }

      return cardItems;
    }

    async fetchPageData(baseUrl, pageNumber) {
      const url = new URL(baseUrl.toString(), window.location.origin);
      if (pageNumber <= 1) {
        url.searchParams.delete('page');
      } else {
        url.searchParams.set('page', String(pageNumber));
      }

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error('Sortierseite konnte nicht geladen werden: HTTP ' + response.status);
      }

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const productGrid = documentFragment.querySelector('.collection-section .product-grid') ||
        documentFragment.querySelector('.product-grid');

      if (!productGrid) {
        return {
          cardItems: [],
          totalPages: 1
        };
      }

      const totalPages = Math.max(1, Number.parseInt(productGrid.dataset.totalPages || '1', 10) || 1);
      const cardItems = Array.from(productGrid.querySelectorAll('.product-card')).map((card) => ({
        html: card.outerHTML,
        ps: this.extractPsValue(card)
      }));

      return {
        cardItems,
        totalPages
      };
    }

    extractPsValue(card) {
      const directValue = card.getAttribute('data-product-ps') || card.getAttribute('data-meta-ps') || '';
      const parsedDirectValue = this.parseNumber(directValue);
      if (Number.isFinite(parsedDirectValue)) return parsedDirectValue;

      const titleText = card.querySelector('.product-card__title a, .product-card__title')?.textContent || '';
      return this.parseNumber(titleText);
    }

    parseNumber(value) {
      const raw = String(value || '').trim();
      if (!raw) return Number.NaN;

      const normalized = raw
        .replace(/\u00a0/g, ' ')
        .replace(/\s+/g, '')
        .replace(',', '.');

      const match = normalized.match(/-?\d+(?:\.\d+)?/);
      if (!match) return Number.NaN;

      const parsed = Number.parseFloat(match[0]);
      return Number.isFinite(parsed) ? parsed : Number.NaN;
    }

    sortCardItems(cardItems, sortValue) {
      const descending = sortValue === 'ps-descending';

      cardItems.sort((left, right) => {
        const leftMissing = !Number.isFinite(left.ps);
        const rightMissing = !Number.isFinite(right.ps);

        if (leftMissing && rightMissing) {
          return left.sourceIndex - right.sourceIndex;
        }

        if (leftMissing) return 1;
        if (rightMissing) return -1;

        const delta = descending ? right.ps - left.ps : left.ps - right.ps;
        if (delta !== 0) return delta;
        return left.sourceIndex - right.sourceIndex;
      });
    }

    getGridElement() {
      return document.querySelector('.collection-section .product-grid') || document.querySelector('.product-grid');
    }

    getPageSizeFromGrid(grid) {
      return Math.max(1, Number.parseInt(grid?.dataset?.pageSize || String(DEFAULT_PAGE_SIZE), 10) || DEFAULT_PAGE_SIZE);
    }

    readPageFromUrl() {
      const page = Number.parseInt(new URL(window.location.href).searchParams.get('page') || '1', 10);
      return Number.isFinite(page) && page > 0 ? page : 1;
    }

    renderPsSortedPage(requestedPage, options = {}) {
      if (!this.psSortState?.active) return;

      const syncHistory = options.syncHistory !== false;
      const grid = this.getGridElement();
      if (!grid) return;

      const totalPages = Math.max(1, this.psSortState.totalPages);
      const safePage = Math.min(totalPages, Math.max(1, Number.parseInt(requestedPage, 10) || 1));
      const pageSize = Math.max(1, this.psSortState.pageSize || DEFAULT_PAGE_SIZE);
      const startIndex = (safePage - 1) * pageSize;
      const pageItems = this.psSortState.cardItems.slice(startIndex, startIndex + pageSize);

      if (!pageItems.length) {
        grid.innerHTML = '<p style="grid-column:1/-1;text-align:center;padding:2rem;color:var(--text-light,#999);">Keine Produkte gefunden.</p>';
      } else {
        grid.innerHTML = pageItems.map((item) => item.html).join('');
      }

      grid.dataset.pageSize = String(pageSize);
      grid.dataset.totalPages = String(totalPages);
      grid.dataset.currentPage = String(safePage);

      this.psSortState.currentPage = safePage;
      this.updateProductCount(this.psSortState.cardItems.length);
      this.renderPsPagination(safePage, totalPages);

      if (syncHistory) {
        this.syncPsSortUrl(safePage);
      }
    }

    updateProductCount(count) {
      const safeCount = Math.max(0, Number.parseInt(count, 10) || 0);
      document.querySelectorAll('[data-product-count], .collection-topbar__count strong').forEach((element) => {
        element.textContent = String(safeCount);
      });
    }

    ensurePaginationElement() {
      const existing = document.querySelector('.collection-pagination.custom-pagination');
      if (existing) return existing;

      const container = document.querySelector('.product-grid-container');
      if (!container || !container.parentNode) return null;

      const pagination = document.createElement('nav');
      pagination.className = 'collection-pagination custom-pagination';
      pagination.setAttribute('aria-label', 'Seitennavigation');
      pagination.innerHTML = '<div class="custom-pagination__pages"></div>';

      const layout = container.closest('.collection-layout');
      if (layout && layout.parentNode) {
        layout.parentNode.insertBefore(pagination, layout.nextSibling);
      } else {
        container.parentNode.insertBefore(pagination, container.nextSibling);
      }

      return pagination;
    }

    buildPaginationParts(currentPage, totalPages) {
      if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_entry, index) => index + 1);
      }

      const parts = [1];
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      if (start > 2) parts.push('ellipsis');
      for (let page = start; page <= end; page += 1) {
        parts.push(page);
      }
      if (end < totalPages - 1) parts.push('ellipsis');
      parts.push(totalPages);

      return parts;
    }

    renderPsPagination(currentPage, totalPages) {
      const pagination = this.ensurePaginationElement();
      if (!pagination) return;

      if (totalPages < 2) {
        pagination.style.display = 'none';
        return;
      }

      pagination.style.display = '';
      const parts = this.buildPaginationParts(currentPage, totalPages);
      const previousPage = currentPage - 1;
      const nextPage = currentPage + 1;

      let html = '';

      if (previousPage >= 1) {
        html += '<a href="' + this.buildPsPageUrl(previousPage) + '" data-a24-ps-sort-page="' + previousPage + '" class="custom-pagination__nav custom-pagination__nav--prev" aria-label="Zur vorherigen Seite">';
        html += '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"></polyline></svg>';
        html += '<span class="pagination__button-text">Zurueck</span>';
        html += '</a>';
      } else {
        html += '<span class="custom-pagination__nav is-disabled" aria-disabled="true">';
        html += '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"></polyline></svg>';
        html += '<span class="pagination__button-text">Zurueck</span>';
        html += '</span>';
      }

      parts.forEach((part) => {
        if (part === 'ellipsis') {
          html += '<span class="custom-pagination__ellipsis">...</span>';
          return;
        }

        if (part === currentPage) {
          html += '<span class="custom-pagination__page is-active" aria-current="page">' + part + '</span>';
          return;
        }

        html += '<a href="' + this.buildPsPageUrl(part) + '" data-a24-ps-sort-page="' + part + '" class="custom-pagination__page" aria-label="Seite ' + part + '">' + part + '</a>';
      });

      if (nextPage <= totalPages) {
        html += '<a href="' + this.buildPsPageUrl(nextPage) + '" data-a24-ps-sort-page="' + nextPage + '" class="custom-pagination__nav custom-pagination__nav--next" aria-label="Zur naechsten Seite">';
        html += '<span class="pagination__button-text">Weiter</span>';
        html += '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>';
        html += '</a>';
      } else {
        html += '<span class="custom-pagination__nav is-disabled" aria-disabled="true">';
        html += '<span class="pagination__button-text">Weiter</span>';
        html += '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"></polyline></svg>';
        html += '</span>';
      }

      const pagesWrapper = pagination.querySelector('.custom-pagination__pages');
      if (pagesWrapper) {
        pagesWrapper.innerHTML = html;
      } else {
        pagination.innerHTML = '<div class="custom-pagination__pages">' + html + '</div>';
      }
    }

    buildPsPageUrl(page) {
      const url = new URL(window.location.href);
      if (this.psSortState?.sortValue) {
        url.searchParams.set('sort_by', this.psSortState.sortValue);
      }

      if (page <= 1) {
        url.searchParams.delete('page');
      } else {
        url.searchParams.set('page', String(page));
      }

      return url.toString();
    }

    syncPsSortUrl(page) {
      const url = new URL(window.location.href);
      if (this.psSortState?.sortValue) {
        url.searchParams.set('sort_by', this.psSortState.sortValue);
      }

      if (page <= 1) {
        url.searchParams.delete('page');
      } else {
        url.searchParams.set('page', String(page));
      }

      history.replaceState(history.state, '', url.toString());
    }

    handleDocumentClick(event) {
      if (!this.psSortState?.active) return;

      const link = event.target?.closest?.('a[data-a24-ps-sort-page]');
      if (!link) return;

      event.preventDefault();

      const page = Number.parseInt(link.getAttribute('data-a24-ps-sort-page') || '1', 10);
      this.renderPsSortedPage(page, { syncHistory: true });

      const grid = this.getGridElement();
      if (grid) {
        grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  customElements.define('a24-product-sort', A24ProductSort);
})();
